import 'react-toastify/dist/ReactToastify.css';

import { HEADER_HEIGHT } from '@utils/constants';
import { FC, StrictMode, useEffect, useRef, useState } from 'react';
import { Slide, ToastContainer } from 'react-toastify';

import { Grid } from '@components/Grid';
import { ReactiveSpectrumProvider } from '@components/ReactiveSpectrumProvider';
import { GlobalStatusDialog } from '@components/SystemAlerts/StatusDialog/GlobalStatusDialog';
import { gray } from '@components/Theme/newBaseColors';
import { useFlagMe480232CarrierPortalNavigationBrandingSearchFe } from '@generated/flags/ME-480232-carrier-portal-navigation-branding-search-fe';
import { UserPreferences } from '@hooks/UserPreferences';
import { queryParamOptions } from '@hooks/useQueryParams';
import { useFullstory } from '@utils/fullstory';
import { setAppRouter } from '@utils/history';
import { win } from '@utils/win';
import { APP_HEADER, REACH_MENU } from '@utils/zIndex';
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
} from 'react-router-dom';
import { QueryParamProvider } from 'use-query-params';
import { ReactRouter6Adapter } from 'use-query-params/adapters/react-router-6';
import { GlobalVariables } from '../../app/GlobalVariables';
import { AppStyles } from '../../app/Styles';
import { MasteryKeycloakProvider } from '../../app/auth/keycloak';
import { SaveProvider } from '../../components/SaveContext';
import { Theme } from '../../components/Theme';
import { Sidebar } from '../common/Sidebar';
import { WalkMeInitializer } from '../components/WalkMeInitializer';
import { AppRoutes } from '../routes';
import { CustomerLandingHeader } from '../views/customer-landing/header/CustomerLandingHeader';
import { Header } from '../views/customer-landing/header/Header';
import { AuthWrapper } from './AuthWrapper';
import { PermissionsProvider } from './Permissions';
import { TenantConfig } from './TenantConfig';

const RootLayout: FC = () => {
  const location = useLocation();
  const path = location?.pathname;
  const enableSidebarNavigation =
    useFlagMe480232CarrierPortalNavigationBrandingSearchFe();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    () => enableSidebarNavigation && win.innerWidth <= 1300,
  );
  const onSidebarToggle = (): void => setIsSidebarCollapsed((prev) => !prev);

  // Measure header height and expose as CSS variable so the sidebar
  // can push its content below the header on mobile/tablet overlay mode.
  const headerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!headerRef.current) {
      return;
    }
    const update = (): void => {
      const h = headerRef.current?.offsetHeight ?? 0;
      document.documentElement.style.setProperty(
        '--carrier-header-height',
        `${h}px`,
      );
    };
    update();
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const ro = new ResizeObserver(update);
    ro.observe(headerRef.current);
    return () => ro.disconnect();
  }, []);

  return (
    <QueryParamProvider
      adapter={ReactRouter6Adapter}
      options={queryParamOptions}
    >
      <AuthWrapper>
        <UserPreferences>
          <PermissionsProvider>
            <TenantConfig>
              <Grid
                gap={0}
                css={{
                  display: 'flex',
                  flexDirection: 'column',
                  height: `calc(100vh - ${HEADER_HEIGHT}px)`,
                  backgroundColor: enableSidebarNavigation
                    ? gray.white
                    : path !== '/'
                      ? gray[95]
                      : 'initial',
                  overflow: 'hidden',
                }}
              >
                {/* Header wrapper — z-index keeps toggle above the fixed sidebar drawer */}
                <div
                  ref={headerRef}
                  css={{ padding: 0, position: 'relative', zIndex: REACH_MENU }}
                >
                  {path === '/' && !enableSidebarNavigation ? (
                    <CustomerLandingHeader />
                  ) : enableSidebarNavigation ? (
                    <Header
                      isCollapsed={isSidebarCollapsed}
                      onSidebarToggle={onSidebarToggle}
                      showSearchBar={path !== '/'}
                    />
                  ) : (
                    <Header />
                  )}
                </div>
                {enableSidebarNavigation ? (
                  <div
                    css={{
                      display: 'flex',
                      flex: 1,
                      overflow: 'hidden',
                    }}
                  >
                    <Sidebar
                      isCollapsed={isSidebarCollapsed}
                      onClose={() => setIsSidebarCollapsed(true)}
                    />
                    {/* Backdrop: closes drawer when tapping outside on mobile/tablet */}
                    {!isSidebarCollapsed && (
                      <div
                        css={{
                          display: 'none',
                          '@media (max-width: 1300px)': {
                            display: 'block',
                            position: 'fixed',
                            top: HEADER_HEIGHT,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            zIndex: APP_HEADER - 1,
                          },
                        }}
                        onClick={onSidebarToggle}
                        data-testid="sidebar-backdrop"
                      />
                    )}
                    <div
                      data-testid="sidebar-content-area"
                      css={{
                        flex: 1,
                        minWidth: 0,
                        overflowX: 'hidden',
                        overflowY: 'auto',
                      }}
                    >
                      <AppRoutes />
                      <GlobalStatusDialog />
                      <WalkMeInitializer />
                    </div>
                  </div>
                ) : (
                  <div
                    css={{
                      flex: 1,
                      overflowX: 'hidden',
                      overflowY: 'auto',
                    }}
                  >
                    <AppRoutes />
                    <GlobalStatusDialog />
                    <WalkMeInitializer />
                  </div>
                )}
              </Grid>
            </TenantConfig>
          </PermissionsProvider>
        </UserPreferences>
      </AuthWrapper>
    </QueryParamProvider>
  );
};

const router = createBrowserRouter([{ path: '*', element: <RootLayout /> }]);

// Wire the data router into the imperative navigation helpers in @utils/history.
setAppRouter(router);

export const App: FC = () => {
  useFullstory();

  return (
    <StrictMode>
      <GlobalVariables />
      <SaveProvider>
        <ReactiveSpectrumProvider id="spectrum-provider">
          <Theme>
            <AppStyles />
            <ToastContainer
              limit={1}
              newestOnTop
              closeButton={false}
              closeOnClick={false}
              style={{ width: '100%', maxWidth: 1000 }}
              transition={Slide}
              containerId={'systemBanner'}
              position="top-center"
            />
            <ToastContainer
              limit={4}
              closeButton={false}
              closeOnClick={false}
              style={{ width: 250 }}
              transition={Slide}
              containerId={'toast'}
            />
            <MasteryKeycloakProvider>
              <RouterProvider router={router} />
            </MasteryKeycloakProvider>
          </Theme>
        </ReactiveSpectrumProvider>
      </SaveProvider>
    </StrictMode>
  );
};
